/**
 * Roadside accessorial builder
 */

import { AccessorialType } from '../../constants/accessorial-types.js'
import type { Accessorial } from '../../types/accessorial.js'
import type { BuildRoadsideOptions } from './types.js'
import { calcDelayPrice, calcDelayPay } from '../shared/calculations.js'
import { buildAccessorial } from '../shared/buildAccessorial.js'

/**
 * Build Roadside Accessorial
 *
 * Used when roadside assistance is needed (flat tire, dead battery, etc.)
 *
 * Cost Responsibility Breakdown:
 * - HopDrive: Pays actual roadside assistance cost
 * - Customer: Pays roadside cost + delay time if responsible
 * - Driver: Paid for idle time during roadside assistance
 *
 * @param options - Roadside accessorial options
 * @returns Insertable roadside accessorial object
 * @throws Error if validation fails
 *
 * @example
 * ```typescript
 * const roadsideAccessorial = buildRoadsideAccessorial({
 *   moveId: 12345,
 *   customerResponsible: true,
 *   roadsideCost: 75.00,
 *   driverIdleTimeMinutes: 45,
 *   customerPricePerMinute: 0.33,
 *   driverPayPerMinute: 0.17
 * })
 * // Result: { cost: 75.00, ar_amount: 89.85, ap_amount: 7.65, ... }
 * ```
 */
export function buildRoadsideAccessorial(options: BuildRoadsideOptions): Accessorial {
  // Validation for required props
  if (!options || typeof options !== 'object') {
    throw new Error('Failed to build roadside accessorial: Invalid options prop!')
  }
  if (!options.moveId || (typeof options.moveId !== 'number' && typeof options.moveId !== 'bigint')) {
    throw new Error('Failed to build roadside accessorial: Invalid moveId prop!')
  }
  if (typeof options.customerResponsible !== 'boolean') {
    throw new Error('Failed to build roadside accessorial: Invalid customerResponsible prop!')
  }
  if (typeof options.roadsideCost !== 'number') {
    throw new Error('Failed to build roadside accessorial: Invalid roadsideCost prop!')
  }

  // Validation for optional props
  if (options.driverIdleTimeMinutes !== undefined && typeof options.driverIdleTimeMinutes !== 'number') {
    throw new Error('Failed to build roadside accessorial: Invalid driverIdleTimeMinutes prop!')
  }
  if (options.customerPricePerMinute !== undefined && typeof options.customerPricePerMinute !== 'number') {
    throw new Error('Failed to build roadside accessorial: Invalid customerPricePerMinute prop!')
  }
  if (options.driverPayPerMinute !== undefined && typeof options.driverPayPerMinute !== 'number') {
    throw new Error('Failed to build roadside accessorial: Invalid driverPayPerMinute prop!')
  }
  if (options.notes !== undefined && options.notes !== null && typeof options.notes !== 'string') {
    throw new Error('Failed to build roadside accessorial: Invalid notes prop!')
  }

  // Calculate roadside & delay price to customer (if customer is responsible)
  const roadsidePrice = options.customerResponsible ? options.roadsideCost : 0
  const delayPrice = options.customerResponsible
    ? calcDelayPrice(options.driverIdleTimeMinutes || 0, options.customerPricePerMinute)
    : 0

  // Calculate delay pay to driver
  const delayPay = calcDelayPay(options.driverIdleTimeMinutes || 0, options.driverPayPerMinute)

  // Build accessorial using base builder
  return buildAccessorial({
    moveId: options.moveId,
    code: AccessorialType.ROADSIDE.code,
    cost: options.roadsideCost,
    ar_amount: roadsidePrice + delayPrice,
    ap_amount: delayPay,
    notes: options.notes || AccessorialType.ROADSIDE.desc,
  })
}
